import { OrderDetail } from "@api/throughShipment/onlineOrderApi";
import { OrderStatusLabel } from "@constant/OrderStatus";
import { InvoiceMode } from "@constant/ThroughShipment";
import useCityRegionCode from "@hooks/useCityRegionCode";
import CarrierTypeValue, { URMART_CARRIER_NUMBER } from "@page/Order/CarrierTypeValue";

import React from "react";
import moment from "moment";
import styled from "styled-components";

const Wrapper = styled.div`
  padding-left: 16px;
  margin-top: 10px;
`;

const Row = styled.div`
  margin-bottom: 20px;
  display: flex;
`;
const RowTitle = styled.span`
  margin-right: 30px;
  width: 110px;
`;

type Props = {
  onlineOrderDetail: OrderDetail;
  hideUserInfo: boolean;
};

const warehouseMap: { [key: string]: string } = {
  RENTRAP: "藍田",
  TCAT: "黑貓",
};

const OrderTab = ({ onlineOrderDetail, hideUserInfo }: Props) => {
  const { invoiceMode } = onlineOrderDetail;
  const { getPostCodeWithCVS } = useCityRegionCode();
  const addressInfos = getPostCodeWithCVS(onlineOrderDetail.receiverAddress);

  const carrierTypeRender = (carrierType: number | undefined): string => {
    if (carrierType === undefined) return "";
    switch (carrierType) {
      case CarrierTypeValue.PHONECODE:
        return "手機載具";
      case CarrierTypeValue.NATURALCODE:
        return "自然人憑證";
      default:
        return "會員載具";
    }
  };

  const carrierNumberRender = (carrierNumber: string | undefined): React.ReactNode => {
    if (invoiceMode !== InvoiceMode.AUTO_INVOICE || !carrierNumber) return null;
    return carrierNumber === URMART_CARRIER_NUMBER ? (
      <span>選擇會員載具，中獎發票將會以E-mail通知客戶</span>
    ) : (
      <span>{carrierNumber}</span>
    );
  };

  return (
    <Wrapper>
      <Row>
        <RowTitle>訂單編號</RowTitle>
        <span>{onlineOrderDetail.orderNumber}</span>
      </Row>
      <Row>
        <RowTitle>建立人員</RowTitle>
        <span>{onlineOrderDetail.staff}</span>
      </Row>
      <Row>
        <RowTitle>建立時間</RowTitle>
        <span>{onlineOrderDetail.createdAt}</span>
      </Row>
      <Row>
        <RowTitle>提交時間</RowTitle>
        <span>{onlineOrderDetail.submittedAt}</span>
      </Row>
      <Row>
        <RowTitle>訂單狀態</RowTitle>
        <span>{OrderStatusLabel[onlineOrderDetail.status]}</span>
      </Row>
      <Row>
        <RowTitle>出庫倉庫</RowTitle>
        <span>{warehouseMap[onlineOrderDetail.warehouseCode]}</span>
      </Row>
      <Row>
        <RowTitle>通路平台</RowTitle>
        <span>{onlineOrderDetail.platform}</span>
      </Row>
      <Row>
        <RowTitle>訂單總金額</RowTitle>
        <span>{onlineOrderDetail.grandTotal}</span>
      </Row>
      <Row>
        <RowTitle>預計出貨日</RowTitle>
        <span>{onlineOrderDetail.estimatedShippingDate}</span>
      </Row>
      <Row>
        <RowTitle>預計到貨日</RowTitle>
        <span>{onlineOrderDetail.estimatedArrivalDate}</span>
      </Row>
      <Row>
        <RowTitle>平台訂單編號</RowTitle>
        <span>{onlineOrderDetail.platformNumber}</span>
      </Row>
      <Row>
        <RowTitle>運送方式</RowTitle>
        <span>{onlineOrderDetail.shippingMethod}</span>
      </Row>
      <Row>
        <RowTitle>收件人</RowTitle>
        <span>{hideUserInfo ? `${onlineOrderDetail.receiverName[0]}**` : onlineOrderDetail.receiverName}</span>
      </Row>
      <Row>
        <RowTitle>收件電話</RowTitle>
        <span>
          {hideUserInfo
            ? `${onlineOrderDetail.receiverPhone.slice(0, 2)}*****${onlineOrderDetail.receiverPhone.slice(8)}`
            : onlineOrderDetail.receiverPhone}
        </span>
      </Row>
      <Row>
        <RowTitle>收件地址</RowTitle>
        {hideUserInfo ? (
          `${addressInfos.code}${addressInfos.city}${addressInfos.town}***`
        ) : (
          <span>
            {onlineOrderDetail.receiverZipcode}
            {onlineOrderDetail.receiverAddress}
          </span>
        )}
      </Row>
      {invoiceMode === InvoiceMode.AUTO_INVOICE && (
        <>
          <Row>
            <RowTitle>收件人Email</RowTitle>
            <span>{onlineOrderDetail.receiverEmail}</span>
          </Row>
          <Row>
            <RowTitle>發票類型</RowTitle>
            <span>{carrierTypeRender(onlineOrderDetail.carrierType)}</span>
          </Row>
          <Row>
            <RowTitle>載具/自然人憑證</RowTitle>
            {carrierNumberRender(onlineOrderDetail.carrierNumber)}
          </Row>
        </>
      )}
      {invoiceMode === InvoiceMode.THIRD_PARTY_FILL && (
        <>
          <Row>
            <RowTitle>平台發票號碼</RowTitle>
            <span>{onlineOrderDetail.invoiceNumber}</span>
          </Row>
          <Row>
            <RowTitle>發票開立日期</RowTitle>
            <span>{onlineOrderDetail.invoiceAt && moment(onlineOrderDetail.invoiceAt).format("YYYY-MM-DD")}</span>
          </Row>
        </>
      )}
      <Row>
        <RowTitle>運送編號</RowTitle>
        <span>{onlineOrderDetail.trackingNumber}</span>
      </Row>
      <Row>
        <RowTitle>備註</RowTitle>
        <span>{onlineOrderDetail.note}</span>
      </Row>
    </Wrapper>
  );
};

export default OrderTab;
